import { type ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface DetailPageLayoutProps {
  header?: ReactNode;
  children: ReactNode;
  /**
   * Optional right-rail content. When provided, the content area renders as a
   * 2-column shell (main + sticky right rail) mirroring the job create form
   * (jobs/new.tsx). When omitted, the layout stays single-column (back-compat
   * for all other detail callers). NOT-297.
   */
  rail?: ReactNode;
  /**
   * Full-width strip above the main column AND the rail (e.g. a tab bar). Inside
   * the main column a long tab row runs into the rail and ends up painted over
   * by it; up here the rail starts level with the content instead.
   */
  toolbar?: ReactNode;
  className?: string;
  contentClassName?: string;
  headerClassName?: string;
}

export function DetailPageLayout({
  header,
  children,
  rail,
  toolbar,
  className,
  contentClassName,
  headerClassName,
}: DetailPageLayoutProps) {
  return (
    <div className={cn('page-enter flex h-full flex-col', className)}>
      {header != null ? (
        <div
          className={cn(
            'flex shrink-0 items-center justify-between border-b py-3 pl-5 pr-4',
            headerClassName,
          )}
        >
          {header}
        </div>
      ) : null}
      {rail != null ? (
        <div className={cn('flex-1 overflow-y-auto', contentClassName)}>
          {/* NOT-297: full-width, denser layout — main grows to fill the viewport
              with a fixed sticky rail, instead of a narrow centered column. */}
          <div className="w-full px-5 py-4">
            {toolbar}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
              <div className="min-w-0 space-y-4">{children}</div>
              {/* A sticky toolbar holds the top of the scroll area, so the rail
                  pins below it rather than sliding underneath. */}
              <aside
                className={cn(
                  'space-y-4 lg:sticky lg:self-start',
                  toolbar != null ? 'lg:top-16' : 'lg:top-4',
                )}
              >
                {rail}
              </aside>
            </div>
          </div>
        </div>
      ) : (
        <div className={cn('flex-1 space-y-5 overflow-y-auto px-5 py-5', contentClassName)}>
          {toolbar}
          {children}
        </div>
      )}
    </div>
  );
}
